import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import ReactDOM from 'react-dom';

import * as Routes from '@/modules/routes';
import Edit from '../../common/Classes/Edit';
import Delete from '../../common/Classes/Delete';
import { formatProgramsForSelect } from '../../common/Classes/FormUtils';
import TeacherModal from '../../common/Classes/TeacherModal';
import { matchAttributesForSingleRow } from '../../common/DataTable/Utils';
import PaginatedTable from '../../common/DataTable/PaginatedTable';
import AdvancedFilters from '../../common/AdvancedFilters';
import { buildFilterPath, filtersPropTypes, propertyPropTypes } from '../../common/AdvancedFilters/Utils';

import { isAdmin } from '../../common/Utils';
import StudentModal from './StudentModal';
import ClassesAdminDropdown from './ClassesAdminDropdown';
import ManageProgramsModal from './ManageProgramsModal';
import Add from './Add';

const CLASSROOM_ATTRIBUTES = [
  'full_name',
  'has_staffer_excluded_from_rostering',
  'id',
  'name',
  'period',
  'program',
  'provider',
  'seat_pool_id',
  'student_seats_count',
  'teachers',
  'term_end_date',
  'term_start_date',
  'uid',
];

// Eager-loads for the associations hit by CLASSROOM_ATTRIBUTES
const CLASSROOM_INCLUDES = ['staffers', 'student_seats', 'program'];

const PAGE_SIZE = 250;

export default class ClassesTable extends Component {
  static propTypes = {
    columns: PropTypes.instanceOf(Array).isRequired,
    initialFilters: filtersPropTypes,
    properties: PropTypes.arrayOf(propertyPropTypes).isRequired,
    subscriberId: PropTypes.number.isRequired,
    userType: PropTypes.string.isRequired
  };

  static defaultProps = {
    initialFilters: null,
  };

  static _processResources(resources) {
    return resources.map(resource => ClassesTable._processResource(resource));
  }

  static _processResource(resource) {
    return {
      ...resource,
      program: resource.program.full_title_with_edition,
      programId: resource.program.id,
      students: resource.student_seats_count,
      actions: ''
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      filterSent: false,
      filtersKey: 0,
      getPath: this._buildAjaxPath(),
      injectedFilters: null,
      pagesLoaded: 0,
      programsLoaded: false,
      resources: [],
      doneLoading: false,
      teachers: [],
      teachersLoaded: false,
      addModalIsOpen: false,
      deleteModalIsOpen: false,
      editModalIsOpen: false,
      manageProgramsModalIsOpen: false,
      studentsModalIsOpen: false,
      teachersModalIsOpen: false,
    };

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.updateTeachers = this.updateTeachers.bind(this);
    this.updateClassroom = this.updateClassroom.bind(this);
    this.updateStudentCount = this.updateStudentCount.bind(this);
    this.removeClassroom = this.removeClassroom.bind(this);
    this.addRow = this.addRow.bind(this);
    this.addResources = this.addResources.bind(this);
    this.resetResources = this.resetResources.bind(this);
    this.updateActiveFilters = this.updateActiveFilters.bind(this);
    this.hyperlinkStudents = this.hyperlinkStudents.bind(this);
  }

  componentDidMount() {
    this._getSubscriberStaffers();
    this._getSubscriberPrograms();
  }

  _buildAjaxPath() {
    return Routes.plato_api_subscriber_classrooms_path(this.props.subscriberId, {
      active: true,
      column: 'columnName',
      includes: CLASSROOM_INCLUDES,
      only: CLASSROOM_ATTRIBUTES,
      order: 'sortDirection',
      page: 'pageNumber',
      per_page: PAGE_SIZE,
    });
  }

  addResources(data) {
    const newResources = ClassesTable._processResources(data);

    const state = {
      doneLoading: newResources.length < PAGE_SIZE,
      pagesLoaded: this.state.pagesLoaded + 1,
      resources: [...this.state.resources].concat(newResources),
    };

    if (this.state.filterSent) {
      state.doneLoading = false;
      state.filterSent = false;
    }

    this.setState(state);
  }

  resetResources() {
    this.setState({ resources: [], doneLoading: false, pagesLoaded: 0 });
  }

  addRow(updatedObject) {
    matchAttributesForSingleRow(updatedObject, this.props.columns);
    const updatedResources = [...this.state.resources].concat(updatedObject);

    this.setState({ resources: updatedResources });
  }

  openModal(e, rowData, type) {
    e.preventDefault();
    this.setState({
      [`${type}ModalIsOpen`]: true,
      rowData
    });
  }

  closeModal(type) {
    this.setState({
      [`${type}ModalIsOpen`]: false,
      rowData: null
    });
  }

  updateActiveFilters(activeFilters) {
    this.setState(
      { activeFilters },
      () => this._showFilteredResources()
    );
  }

  _showFilteredResources() {
    const getPath = buildFilterPath(this._buildAjaxPath(), this.state.activeFilters);

    this.setState(prevState => ({
      doneLoading: prevState.filterSent || !prevState.doneLoading,
      filterSent: true,
      getPath,
      loadId: prevState.loadId + 1,
      pagesLoaded: 0,
      resources: []
    }));
  }

  updateClassroom(data) {
    const updatedResources = this.state.resources.map((classroom) => {
      if (classroom.id !== data.id) {
        return classroom;
      }
      return ClassesTable._processResource(data);
    });

    this.setState({ resources: updatedResources });
  }

  updateStudentCount(data) {
    const updatedResources = this.state.resources.map((classroom) => {
      if (classroom.id !== data.id) {
        return Object.assign({}, classroom);
      }

      const numAddedStudents = data.addedStudents.length;
      const numRemovedStudents = data.removedStudents.length;

      return {
        ...classroom,
        students: classroom.students + numAddedStudents - numRemovedStudents
      };
    });

    this.setState({ resources: updatedResources });
  }

  updateTeachers(data) {
    const updatedResources = this.state.resources.map((classroom) => {
      if (classroom.id !== data.id) {
        return Object.assign({}, classroom);
      }

      return {
        ...classroom,
        teachers: data.teachers,
      };
    });

    this.setState({ resources: updatedResources });
  }

  removeClassroom(data) {
    const updatedResources = this.state.resources.filter(classroom => classroom.id !== data.id);

    this.setState({ resources: updatedResources });
  }

  _getSubscriberStaffers() {
    if (this.state.teachers.length) return;

    Axios
      .get(Routes.plato_api_subscriber_staffers_path(this.props.subscriberId, {
        only: ['id', 'first_name', 'last_name', 'email'],
      }))
      .then(response => this.setState({ teachers: response.data.data, teachersLoaded: true }))
      .catch((error) => {
        console.log(error);
        this.setState({ teachersLoaded: true });
      });
  }

  _getSubscriberPrograms() {
    Axios
      .get(Routes.plato_api_subscriber_programs_path(this.props.subscriberId, {
        active_for_sub: this.props.subscriberId,
        only: ['id', 'full_title_with_edition'],
      }))
      .then((response) => {
        const programs = formatProgramsForSelect(response.data.data);
        this.setState({ programs, programsLoaded: true });
      })
      .catch((error) => {
        console.log(error);
        this.setState({ programsLoaded: true });
      });
  }

  _renderDelete() {
    if (this.state.deleteModalIsOpen) {
      return (
        <Delete
          closeModal={() => this.closeModal('delete')}
          destroyPath={Routes.plato_api_classroom_path(this.state.rowData.id)}
          modalIsOpen={this.state.deleteModalIsOpen}
          rowData={this.state.rowData}
          updateTable={this.removeClassroom}
        />
      );
    }

    return null;
  }

  _renderStudentModal() {
    if (this.state.studentsModalIsOpen) {
      return (
        <StudentModal
          classroomStudentsPath={Routes.plato_api_classroom_students_path(this.state.rowData.id)}
          closeModal={() => this.closeModal('students')}
          manageStudentsPath={Routes.plato_api_manage_students_classroom_path(this.state.rowData.id)}
          modalIsOpen={this.state.studentsModalIsOpen}
          rowData={this.state.rowData}
          searchStudentsPath={Routes.plato_api_search_subscriber_students_path(this.props.subscriberId)}
          updateStudentCount={this.updateStudentCount}
        />
      );
    }

    return null;
  }

  _renderTeachersModal() {
    if (this.state.teachersModalIsOpen) {
      return (
        <TeacherModal
          classroomStaffersPath={Routes.plato_api_classroom_staffers_path(this.state.rowData.id)}
          closeModal={() => this.closeModal('teachers')}
          modalIsOpen={this.state.teachersModalIsOpen}
          rowData={this.state.rowData}
          sharePath={Routes.plato_api_share_classroom_path(this.state.rowData.id)}
          subscriberStaffersPath={Routes.plato_api_subscriber_staffers_path(this.props.subscriberId, {
            only: ['id', 'first_name', 'last_name', 'email'],
          })}
          subscriberTeachers={this.state.teachers}
          updateTeachers={this.updateTeachers}
        />
      );
    }

    return null;
  }

  _renderEditModal() {
    if (this.state.editModalIsOpen) {
      return (
        <Edit
          closeModal={() => this.closeModal('edit')}
          updatePath={Routes.plato_api_subscriber_classroom_path(this.props.subscriberId, this.state.rowData.id)}
          modalIsOpen={this.state.editModalIsOpen}
          rowData={this.state.rowData}
          updateClassroom={this.updateClassroom}
        />
      );
    }

    return null;
  }

  _renderManageProgramsModal() {
    if (this.state.manageProgramsModalIsOpen) {
      return (
        <ManageProgramsModal
          closeModal={() => this.closeModal('managePrograms')}
          isOpen={this.state.manageProgramsModalIsOpen}
          rowData={this.state.rowData}
          subscriberId={this.props.subscriberId}
          onSaveSuccess={() => {
            const { uid } = this.state.rowData;
            this.setState(prev => ({
              filtersKey: prev.filtersKey + 1,
              injectedFilters: [{ operatorValue: 'eq', propertyValue: 'uid', value: uid }],
            }));
            this.closeModal('managePrograms');
          }}
        />
      );
    }

    return null;
  }

  _renderAdvancedFilters() {
    return (
      <div>
        <AdvancedFilters
          key={this.state.filtersKey}
          asyncSearchPath={Routes.plato_api_search_subscriber_classrooms_path(this.props.subscriberId, {
            active: true,
            includes: CLASSROOM_INCLUDES,
            only: CLASSROOM_ATTRIBUTES,
          })}
          initialFilters={this.state.injectedFilters ?? this.props.initialFilters}
          properties={this.props.properties}
          resources={this.state.resources}
          updateActiveFilters={this.updateActiveFilters}
        />
        <hr />
      </div>
    );
  }

  hyperlinkStudents(td, cellData, rowData) {
    if (cellData > 0) {
      const data = {
        initialFilters: [
          {
            propertyValue: 'complete_classrooms_full_name',
            operatorValue: 'eq',
            value: `${rowData.name}, Period ${rowData.period}`
          },
          {
            propertyValue: 'staffers_full_name_with_email',
            operatorValue: 'cont',
            value: `${rowData.teachers.split(',')[0]}`
          }
        ]
      };
      const studentsPath = Routes.admin_subscriber_students_path(this.props.subscriberId);
      ReactDOM.render(
        <a href={`${studentsPath}?${$.param(data)}`}>
          {cellData}
        </a>,
        td
      );
    }

    return cellData;
  }

  _renderAdd() {
    if (isAdmin(this.props.userType)) {
      return (
        <Add
          closeModal={() => this.closeModal('add')}
          createPath={Routes.plato_api_classrooms_path()}
          isLoading={!(this.state.programsLoaded && this.state.teachersLoaded)}
          modalIsOpen={this.state.addModalIsOpen}
          openModal={this.openModal}
          programs={this.state.programs}
          subscriberId={this.props.subscriberId}
          teachers={this.state.teachers}
          updateTable={this.addRow}
        />
      );
    }
    return null;
  }

  render() {
    return (
      <div>
        {this._renderAdvancedFilters()}
        {this._renderAdd()}

        <PaginatedTable
          pinHeightOnAppend
          addResources={this.addResources}
          columns={this.props.columns}
          columnDefs={[
            { targets: [0, 3], width: '20%' },
            { targets: [1, 2], width: '10%' },
            {
              targets: 2,
              title: 'Num of Students',
              createdCell: this.hyperlinkStudents,
              sortable: false
            },
            {
              targets: 5,
              width: '12%',
              sortable: false,
              searchable: false,
              visible: isAdmin(this.props.userType),
              createdCell: (td, cellData, rowData) => {
                ReactDOM.render(
                  <ClassesAdminDropdown openModal={this.openModal} rowData={rowData} />,
                  td
                );
              }
            },
            {
              createdCell: (td, cellData, rowData) => {
                ReactDOM.render(
                  <p>{rowData.uid}</p>,
                  td
                );
              },
              targets: 6,
              title: 'UID',
              visible: false
            }
          ]}
          columnMapping={['name', 'period', null, null, 'program', null, 'uid']}
          doneLoading={this.state.doneLoading}
          exportedColumns={[0, 1, 2, 3, 4, 6]}
          getUrl={this.state.getPath}
          pagesLoaded={this.state.pagesLoaded}
          resetResources={this.resetResources}
          resources={this.state.resources}
        />

        {this._renderStudentModal()}
        {this._renderTeachersModal()}
        {this._renderEditModal()}
        {this._renderDelete()}
        {this._renderManageProgramsModal()}
      </div>
    );
  }
}
